import { type ListResult, OBJECTS, type ObjectName, type RecentItem, recordName, type ReportResult } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { MyWorkList } from '../components/ActivityPanel';
import { AttentionPeek, compactSar } from '../components/AttentionPeek';
import { ExistingDocsForm } from '../components/DealDocuments';
import { firstLevel, ReportChart } from '../components/Charts';
import { ObjectIcon } from '../components/ObjectIcon';
import { RecordForm } from '../components/RecordForm';
import { Skeleton } from '../components/Skeleton';
import { Ring } from '../components/Ring';
import { StageBar, stageColor } from '../components/StageBar';
import { api, qs } from '../lib/api';
import { celebrate, jumpTo, justFinishedTasks, reducedMotion, toast } from '../lib/motion';
import { displayValue, formatDate, formatMoney } from '../lib/format';
import { useObjectDef, usePipelines, useSession, useStages } from '../lib/session';
import { promptDialog } from '../lib/dialogs';
import { type ActivityList, type Goal, HomeHero, ymd } from './home/Target';
import { Icon } from '../components/Icon';

/** Per-user browser preference (collapsed panel, dismissed tips); the page works without it. */
function useStored<T>(key: string, initial: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => {
    try { const s = localStorage.getItem(key); return s ? (JSON.parse(s) as T) : initial; } catch { return initial; }
  });
  const set = (next: T) => { setV(next); try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* storage unavailable */ } };
  return [v, set];
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

// ---------------------------------------------------------------------------
// Welcome panel with getting-started suggestions
// ---------------------------------------------------------------------------

interface Tip { id: string; title: string; text: string; to?: string; create?: ObjectName }

/** How many records of a kind the user can see (undefined while loading or without access). */
function useTotal(o: ObjectName): number | undefined {
  const { permissions } = useSession();
  return useQuery({
    queryKey: ['records', o, 'home-count'],
    enabled: permissions.objects[o].read,
    queryFn: () => api<ListResult>(`/api/records/${o}${qs({ limit: 1 })}`).then((r) => r.total),
  }).data;
}

/** One icon (and colour) per suggestion, so the cards are easy to tell apart. */
const TIP_ICON: Record<string, { path: string; color: string }> = {
  // Upload arrow into a tray.
  import: { path: 'M12 15V4m0 0L8 8m4-4 4 4M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4', color: '#0b7a75' },
  // Person.
  contact: { path: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 8c.8-3.4 3.6-5.5 7-5.5s6.2 2.1 7 5.5', color: '#7c4dce' },
  // Handshake / deal: a tag with a plus.
  deal: { path: 'M3.5 12.5 11 5h8v8l-7.5 7.5a1.4 1.4 0 0 1-2 0l-6-6a1.4 1.4 0 0 1 0-2ZM15.5 8.5h.01', color: '#0b5cab' },
  // Group of people with a plus.
  invite: { path: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm-6 9c.7-3 3-4.8 6-4.8s5.3 1.8 6 4.8M19 8v6m-3-3h6', color: '#2e844a' },
  // Shield with a check.
  security: { path: 'M12 3.5 19 6v5.5c0 4.3-3 7.6-7 9-4-1.4-7-4.7-7-9V6l7-2.5Zm-3 8.5 2.2 2.2L15.5 10', color: '#b42318' },
  // Pipeline board: three columns.
  board: { path: 'M4 5h4v14H4zM10 5h4v9h-4zM16 5h4v6h-4z', color: '#e07a1f' },
  // Key.
  mfa: { path: 'M14.5 9.5a4.5 4.5 0 1 1-1.3-3.2M14.5 9.5H21m-2.5 0v3m-3-3v2M8 9.5h.01', color: '#5b6372' },
};

/** Attention-center cards: one icon and colour per kind, so the deal cards don't all look alike. */
const ATT_ICON: Record<string, { path: string; color: string }> = {
  // Paper plane: quotation ready to send.
  quote_ready: { path: 'M21 3 10 14M21 3l-7 18-4-7-7-4 18-7Z', color: '#0b5cab' },
  // Gavel-like flag: tender due.
  tender_due: { path: 'M5 21V4m0 0h11l-2 4 2 4H5', color: '#7c4dce' },
  // Hourglass: waiting for documents.
  waiting_docs: { path: 'M7 3h10M7 21h10M8 3c0 5 8 5 8 9s-8 4-8 9M16 3c0 5-8 5-8 9', color: '#c2410c' },
  // Document with a question mark: pre-sales needs documents.
  clarification: { path: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Zm0 0v5h5M10.5 12a1.6 1.6 0 1 1 2.2 1.5c-.5.2-.7.6-.7 1v.5M12 17.5h.01', color: '#0b7a75' },
  // Calendar with a cross: past the close date.
  past_due: { path: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4M10 13l4 4M14 13l-4 4', color: '#b42318' },
  // Snowflake: cold deal.
  cold: { path: 'M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9', color: '#3b82c4' },
  // Trophy: won deal without a project.
  won_no_project: { path: 'M8 4h8v5a4 4 0 0 1-8 0V4ZM8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4M12 13v4M8 20h8', color: '#2e844a' },
  // Inbox tray: submittals requested.
  'sub-requests': { path: 'M4 13l2.5-8h11L20 13v6H4v-6Zm0 0h5l1 2h4l1-2h5', color: '#5b6372' },
  // Calendar with an arrow sliding right: deals whose PO date keeps moving later.
  keeps_slipping: { path: 'M4 6h11v13H4zM4 10h11M8 3v4M12 3v4M15 14h6m-3-3 3 3-3 3', color: '#c2410c' },
  // Handshake with a percent: won deals without payment terms.
  no_payment_terms: { path: 'M4 7h16v10H4zM8 14l3-4M8.5 10.5h.01M11 14h.01M14 10h4M14 13h3', color: '#7c4dce' },
  // Percent sign in a circle: won deals with no GP%.
  won_gp_missing: { path: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM9 15l6-6M9.5 9.5h.01M14.5 14.5h.01', color: '#b26a00' },
  // Calendar with a document: wins whose quotation date is the PO day.
  quote_same_day: { path: 'M5 5h14v15H5zM5 9h14M9 3v4M15 3v4M8 13h5M8 16h8', color: '#5b6372' },
  // Receipt: invoices ready to request (Accounts).
  'ready-invoice': { path: 'M6 3h12v18l-3-2-3 2-3-2-3 2V3ZM9 8h6M9 12h6M9 16h3', color: '#1e7b45' },
  // Receipt with a clock: tax invoices owed on paid PIs.
  'pi-tax-owed': { path: 'M6 3h12v8M6 3v18l3-2 3 2M9 8h6M9 12h3M17 14v3l2 1M21 17a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z', color: '#b42318' },
  // Box with a check: submittal ready to send.
  'sub-ready': { path: 'M4 7l8-4 8 4v10l-8 4-8-4V7Zm0 0 8 4 8-4M12 11v10M9 14l2 2 4-4', color: '#a16207' },
};

/** Tours this browser has finished (kept by the Help & guide tour player). */
const toursWatched = (): boolean => { try { const v = JSON.parse(localStorage.getItem('crm.tour.done') ?? '[]') as unknown; return Array.isArray(v) && v.length > 0; } catch { return false; } };

/**
 * First steps for a new user, with a progress ring: each step is ticked when it is opened (the tour when one is
 * watched to the end). Only the steps this person's role can do are listed; it hides once all are done or closed.
 */
function GettingStarted() {
  const { user, permissions } = useSession();
  const [done, setDone] = useStored<string[]>(`home.start.done.${user.id}`, []);
  const [closed, setClosed] = useStored(`home.start.closed.${user.id}`, false);
  const steps: { id: string; label: string; to: string }[] = [
    { id: 'tour', label: 'Watch your video tour', to: '/help?tour=play' },
    ...(permissions.objects.opportunity.read ? [{ id: 'board', label: 'Open the pipeline board', to: '/o/opportunity?view=kanban' }] : []),
    ...(permissions.system.presalesWork ? [{ id: 'presales', label: 'Open My Desk', to: '/presales' }] : []),
    { id: 'calendar', label: 'Look at your calendar', to: '/calendar' },
  ];
  const isDone = (id: string) => done.includes(id) || (id === 'tour' && toursWatched());
  const count = steps.filter((s) => isDone(s.id)).length;
  if (closed || count === steps.length) return null;
  return (
    <div className="start-list" aria-label="Getting started">
      <Ring value={(count / steps.length) * 100} size="xs" tone="blue" center={`${count}/${steps.length}`} title={`${count} of ${steps.length} first steps done`} />
      <div>
        <b>Getting started</b>
        <ul>
          {steps.map((s) => (
            <li key={s.id} className={isDone(s.id) ? 'done' : ''}>
              <Link to={s.to} onClick={() => { if (!done.includes(s.id)) setDone([...done, s.id]); }}>{s.label}</Link>
            </li>
          ))}
        </ul>
      </div>
      <button className="tip-close" aria-label="Hide getting started" onClick={() => setClosed(true)}>×</button>
    </div>
  );
}

function Welcome({ onCreate }: { onCreate: (o: ObjectName) => void }) {
  const { user, permissions } = useSession();
  const [collapsed, setCollapsed] = useStored(`home.welcome.collapsed.${user.id}`, false);
  const [dismissed, setDismissed] = useStored<string[]>(`home.tips.dismissed.${user.id}`, []);
  const counts = { account: useTotal('account'), contact: useTotal('contact'), lead: useTotal('lead'), opportunity: useTotal('opportunity') };
  const admin = permissions.system.manageUsers || permissions.system.manageSetup;

  const tips: Tip[] = [];
  if (admin && counts.account === 0) tips.push({ id: 'import', title: 'Bring in your data', text: 'Import companies, contacts and deals from a CSV file in a few steps.', to: '/import' });
  if (counts.contact === 0 && permissions.objects.contact.create) tips.push({ id: 'contact', title: 'Add your first contact', text: 'Keep the people you work with linked to their companies.', create: 'contact' });
  if (counts.opportunity === 0 && permissions.objects.opportunity.create) tips.push({ id: 'deal', title: 'Add your first deal', text: 'Track each deal through its stages, from qualification to won.', create: 'opportunity' });
  if (admin) tips.push({ id: 'invite', title: 'Invite your team', text: 'Add colleagues, choose what they can do and where they sit in the team.', to: '/setup/users' });
  if (admin) tips.push({ id: 'security', title: 'Protect sign-in', text: 'Require two-step verification and limit sign-in to your office network.', to: '/setup/security' });
  if ((counts.opportunity ?? 0) > 0) tips.push({ id: 'board', title: 'Work your pipeline', text: 'Drag deals between stages on the pipeline board.', to: '/o/opportunity?view=kanban' });
  tips.push({ id: 'mfa', title: 'Secure your account', text: 'Add an authenticator app or a passkey to your own sign-in.', to: '/settings' });
  const shown = tips.filter((t) => !dismissed.includes(t.id)).slice(0, 3);

  return (
    <section className={`welcome${collapsed ? ' collapsed' : ''}${!collapsed && !shown.length ? ' no-tips' : ''}`} aria-label="Welcome">
      <button className="welcome-toggle" aria-expanded={!collapsed} aria-label={collapsed ? 'Show suggestions' : 'Hide suggestions'} onClick={() => setCollapsed(!collapsed)}>
        <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      <div className="welcome-intro">
        <h2 className="welcome-title">Suggestions for you</h2>
        {!collapsed && <GettingStarted />}
      </div>
      {!collapsed && shown.length > 0 && (
        <ul className="tips">
          {shown.map((t) => (
            <li key={t.id} className="tip">
              <span className="tip-badge" aria-hidden="true" style={TIP_ICON[t.id] ? { background: TIP_ICON[t.id]!.color } : undefined}>
                <svg viewBox="0 0 24 24"><path d={TIP_ICON[t.id]?.path ?? 'M12 5.5 20 9l-8 3.5L4 9l8-3.5Zm-5 5.3v3.7c1.4 1.3 3.1 2 5 2s3.6-.7 5-2v-3.7M20 9v4.5'} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
              <button className="tip-close" aria-label={`Dismiss: ${t.title}`} onClick={() => setDismissed([...dismissed, t.id])}>×</button>
              {t.create
                ? <button className="tip-title" onClick={() => onCreate(t.create!)}>{t.title}</button>
                : <Link className="tip-title" to={t.to!}>{t.title}</Link>}
              <p>{t.text}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// "My …" list cards
// ---------------------------------------------------------------------------

const EmptyArt = ({ hue }: { hue: string }) => (
  <svg className="empty-art" viewBox="0 0 240 150" aria-hidden="true">
    <circle cx="120" cy="92" r="70" fill={hue} opacity="0.18" />
    <rect x="62" y="48" width="116" height="78" rx="8" fill="var(--surface)" stroke="var(--line-2)" />
    <rect x="74" y="62" width="40" height="7" rx="3.5" fill="var(--line-2)" />
    <rect x="74" y="78" width="92" height="6" rx="3" fill="var(--line)" />
    <rect x="74" y="92" width="80" height="6" rx="3" fill="var(--line)" />
    <rect x="74" y="106" width="60" height="6" rx="3" fill="var(--line)" />
    <circle cx="160" cy="65" r="9" fill={hue} opacity="0.85" />
  </svg>
);

const CARD_HUE: Partial<Record<ObjectName, string>> = { account: 'var(--obj-account)', lead: 'var(--obj-lead)', opportunity: 'var(--obj-opportunity)', contact: 'var(--obj-contact)' };

function subtitle(o: ObjectName, r: Record<string, unknown>, refs: Record<string, string>, currency: string, fieldsOf: (n: string) => Parameters<typeof displayValue>[0] | undefined): string {
  const show = (name: string) => { const f = fieldsOf(name); return f ? displayValue(f, r[name], refs, currency) : ''; };
  const parts = o === 'lead' ? [show('company'), show('status')]
    : o === 'account' ? [show('type'), show('industry'), show('phone')]
    : o === 'opportunity' ? [show('amount'), show('stageId'), r.closeDate ? `closes ${formatDate(r.closeDate)}` : '']
      : [show('accountId'), show('email') || show('phone')];
  return parts.filter(Boolean).join(' · ');
}

/** What each card's chart counts by: lead status, deal stage, and the contact's account. */
const CHART_GROUP: Partial<Record<ObjectName, string>> = { account: 'type', lead: 'status', opportunity: 'stageId', contact: 'accountId' };

const asOf = (t: number) => (t ? `As of today at ${new Date(t).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}` : '');

function CardMenu({ mode, onMode, onRefresh }: { mode: 'list' | 'chart'; onMode: (m: 'list' | 'chart') => void; onRefresh: () => void }) {
  const [open, setOpen] = useState(false);
  const pick = (fn: () => void) => () => { fn(); setOpen(false); };
  return (
    <div className="card-menu">
      <button className="btn round" aria-label="Card options" aria-expanded={open} onClick={() => setOpen(!open)}>
        <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 4.5 6 8.5l4-4" fill="currentColor" /></svg>
      </button>
      {open && (
        <div className="card-menu-pop" role="menu">
          <button role="menuitemradio" aria-checked={mode === 'list'} onClick={pick(() => onMode('list'))}>Show as list</button>
          <button role="menuitemradio" aria-checked={mode === 'chart'} onClick={pick(() => onMode('chart'))}>Show as chart</button>
          <button role="menuitem" onClick={pick(onRefresh)}>Refresh</button>
        </div>
      )}
    </div>
  );
}

function MyListCard({ o, onCreate }: { o: ObjectName; onCreate: (o: ObjectName) => void }) {
  const { organization, permissions, user } = useSession();
  const obj = useObjectDef(o)!;
  const [q, setQ] = useState('');
  const [byName, setByName] = useState(false);
  const [mode, setMode] = useStored<'list' | 'chart'>(`home.card.${o}.mode.${user.id}`, 'list');
  const term = useDebounced(q.trim(), 250);
  const sort = byName ? (obj.nameField ?? 'lastName') : 'createdAt';
  const list = useQuery({
    queryKey: ['records', o, 'home-mine', term, sort],
    enabled: mode === 'list',
    queryFn: () => api<ListResult>(`/api/records/${o}${qs({ mine: 'true', limit: 6, q: term || undefined, sort, dir: byName ? 'asc' : 'desc' })}`),
  });
  const groupBy = CHART_GROUP[o];
  const chart = useQuery({
    queryKey: ['records', o, 'home-chart'],
    enabled: mode === 'chart' && !!groupBy,
    queryFn: () => api<ReportResult>('/api/reports/run', {
      method: 'POST',
      body: { definition: { object: o, format: 'summary', scope: 'mine', rowGroupings: [{ field: groupBy }], aggregates: [{ fn: 'count' }], showDetails: false } },
    }),
  });
  const active = mode === 'chart' ? chart : list;
  const fieldsOf = (n: string) => obj.fields.find((f) => f.name === n);
  const title = `My ${obj.pluralLabel}`;
  const data = list.data;
  const empty = (
    <div className="list-card-empty">
      {/* While loading: the shape of what is coming (rows, or the chart's bars) instead of the empty picture. */}
      {active.isPending ? <Skeleton shape={mode === 'chart' ? 'bars' : 'list'} label={`Loading ${title.toLowerCase()}…`} /> : <>
        <EmptyArt hue={CARD_HUE[o] ?? 'var(--accent)'} />
        <p>{term && mode === 'list' ? `No ${obj.pluralLabel.toLowerCase()} match “${term}”.` : `No ${obj.pluralLabel.toLowerCase()} assigned to you yet.`}</p></>}
    </div>
  );
  return (
    <section className={`list-card${active.isFetching && !active.isPending ? ' is-updating' : ''}`} aria-label={title} aria-busy={active.isFetching}>
      <header>
        <ObjectIcon name={o} />
        <label className="list-card-search">
          <span className="sr-only">Search {title}</span>
          <input type="search" placeholder={title} value={q} onChange={(e) => { setQ(e.target.value); if (mode === 'chart') setMode('list'); }} />
          <svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="8.5" cy="8.5" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="m13 13 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
        </label>
        {mode === 'list' && (
          <button className="btn square" title={byName ? 'Sorted by name: show newest first' : 'Sorted by newest: sort by name'} aria-label={byName ? 'Sort by newest' : 'Sort by name'} onClick={() => setByName(!byName)}>
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 5h12M4 10h8M4 15h4M15 9v8m0 0-2.5-2.5M15 17l2.5-2.5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        )}
        {permissions.objects[o].create && <button className="btn pill-btn" onClick={() => onCreate(o)}>New</button>}
        <CardMenu mode={mode} onMode={setMode} onRefresh={() => void active.refetch()} />
      </header>
      {mode === 'chart' ? (
        chart.data && firstLevel(chart.data, 0, obj).length ? (
          <div className="list-card-chart">
            <p className="chart-caption">Record count by {fieldsOf(groupBy ?? '')?.label.toLowerCase() ?? 'group'}</p>
            <ReportChart result={chart.data} chart="bar" currency={organization.currency} />
          </div>
        ) : chart.isError ? <p className="muted pad">The chart couldn’t load. Try Refresh.</p> : empty
      ) : data?.records.length ? (
        <ul className="list-card-rows">
          {data.records.map((r) => (
            <li key={r.id}>
              <Link to={`/o/${o}/${r.id}`}>{recordName(obj, r) || '(no name)'}</Link>
              <span className="muted small">{subtitle(o, r, data.refs, organization.currency, fieldsOf)}</span>
            </li>
          ))}
        </ul>
      ) : empty}
      <footer className="card-foot-split">
        <Link to={`/o/${o}?mine=true`}>View all{mode === 'list' && data && data.total > data.records.length ? ` (${data.total})` : ''}</Link>
        <span className="muted small as-of">
          {asOf(active.dataUpdatedAt)}
          <button className="icon-mini" aria-label="Refresh" title="Refresh" onClick={() => void active.refetch()}>
            <svg viewBox="0 0 20 20" aria-hidden="true" className={active.isFetching ? 'spin' : ''}><path d="M16 10a6 6 0 1 1-1.8-4.3M16 3.5v3.7h-3.7" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </span>
      </footer>
    </section>
  );
}

export interface QueueRow {
  id: string; name: string; accountName: string | null; amount: number | null; priority: string | null; priorityStatus: string | null;
  presalesStatus: string | null; presalesUserName: string | null; ownerName: string; dealSource: string | null; createdAt: string;
  hasDocuments?: boolean;
  /** Support engineers who haven't accepted yet (a High priority deal stays in the queue until they do). */
  supportWaiting?: number;
  documentsConfirmed?: boolean;
  /** Open tasks of its pre-sales engineers on it (it stays in the queue while there are any). */
  openTasks?: number;
}

/** Pre-sales (and administrators): High priority deals, then new deals pre-sales hasn't started. Shown instead of My Leads. */
function PresalesQueueCard() {
  const { organization, permissions } = useSession();
  // Administrators: tick deals that already have their full documents and confirm (override "Waiting for documents").
  const admin = permissions.system.manageSetup;
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const confirmDocs = async (ids: string[], confirmed: boolean) => {
    setBusy(true);
    try {
      await api('/api/presales/documents-confirmed', { method: 'POST', body: { ids, confirmed } });
      setPicked(new Set());
      void qc.invalidateQueries({ queryKey: ['presales-queue'] });
      void qc.invalidateQueries({ queryKey: ['attention'] });
    } catch (e) { toast(e instanceof Error ? e.message : 'Could not save.'); }
    setBusy(false);
  };
  // Every deal in the queue: the card's list scrolls when there are more than fit.
  const q = useQuery({ queryKey: ['presales-queue'], queryFn: () => api<{ rows: QueueRow[]; total: number }>('/api/presales/queue?all=1'), refetchInterval: 60_000 });
  const qc = useQueryClient();
  // The queue row whose "documents already in OneDrive" form is open.
  const [openId, setOpenId] = useState<string | null>(null);
  const STARTED: Record<string, string> = { in_progress: 'In progress', clarification: 'Waiting for clarification', quotation_ready: 'Quotation ready' };
  const status = (r: QueueRow) => (r.presalesStatus && STARTED[r.presalesStatus] ? `Pre-sales: ${STARTED[r.presalesStatus]}${r.presalesUserName ? ` (${r.presalesUserName})` : ''}`
    : r.presalesStatus !== 'ready' ? null
    : r.presalesUserName ? `Assigned to ${r.presalesUserName}, not started` : 'With the pre-sales team · to assign');
  // Started deals (High priority ones stay in the queue) need no documents prompt.
  const waiting = (r: QueueRow) => !r.presalesStatus || r.presalesStatus === 'awaiting_documents';
  return (
    <section className="list-card" aria-label="Pre-sales queue">
      <header>
        <ObjectIcon name="opportunity" />
        {/* The title opens the full list of the queue (its own page). */}
        <h3 className="list-card-title">
          <Link className="queue-toggle" to="/presales/queue" title="Open the full list">
            Pre-sales queue {q.data ? <span className="muted">({q.data.total})</span> : null} <span className="queue-caret" aria-hidden="true">›</span>
          </Link>
        </h3>
        {/* The work itself (assign, start, clarification, quotation ready) is done in My Desk. */}
        <Link className="btn small" to="/presales">Open My Desk</Link>
      </header>
      {admin && picked.size > 0 && (
        <div className="queue-confirm">
          <span className="small">{picked.size} selected</span>
          <button className="btn small primary" disabled={busy} onClick={() => void confirmDocs([...picked], true)}>Documents complete</button>
          <button className="btn small ghost" disabled={busy} onClick={() => setPicked(new Set())}>Cancel</button>
        </div>
      )}
      {q.data?.rows.length ? (
        <ul className="list-card-rows queue-scroll">
          {q.data.rows.map((r) => (
            <li key={r.id}>
              <span className="queue-title">
                {admin && waiting(r) && !r.hasDocuments && (
                  <input type="checkbox" className="queue-pick" aria-label={`Documents complete: ${r.name}`} title="Tick if this deal already has its full documents"
                    checked={picked.has(r.id)} onChange={(e) => setPicked((s) => { const n = new Set(s); if (e.target.checked) n.add(r.id); else n.delete(r.id); return n; })} />
                )}
                <Link to={`/o/opportunity/${r.id}`} title={r.name}>{r.name}</Link>
                {r.priority === 'high' && <span className="queue-high">HIGH PRIORITY</span>}
                {r.priority !== 'high' && r.priorityStatus === 'pending' && <span className="queue-pending">High priority requested</span>}
                {admin && r.documentsConfirmed && <button className="link-btn small queue-docs-ok" title="Confirmed by an administrator. Click to undo." disabled={busy} onClick={() => void confirmDocs([r.id], false)}>✓ Documents confirmed (undo)</button>}
                {waiting(r) && !r.hasDocuments && <span className="queue-docs" title="Sales hasn’t uploaded the documents and handed the deal to pre-sales yet">Waiting for documents</span>}
              </span>
              <span className="muted small">
                {[r.accountName, r.dealSource === 'on_hand' ? 'On-Hand' : r.dealSource === 'tender' ? 'Tender' : null,
                  r.amount != null ? formatMoney(r.amount, organization.currency) : null, status(r),
                  r.supportWaiting ? `${r.supportWaiting} support not accepted yet` : null,
                  r.openTasks ? `${r.openTasks} open task${r.openTasks === 1 ? '' : 's'}` : null, `by ${r.ownerName}`].filter(Boolean).join(' · ')}
              </span>
              {waiting(r) && (openId === r.id
                ? <ExistingDocsForm opportunityId={r.id} onCancel={() => setOpenId(null)} onDone={() => { setOpenId(null); void qc.invalidateQueries({ queryKey: ['presales-queue'] }); }} />
                : <button className="link-btn small queue-existing" onClick={() => setOpenId(r.id)}>Documents already in OneDrive?</button>)}
            </li>
          ))}
        </ul>
      ) : q.isPending ? <Skeleton shape="list" label="Loading the pre-sales queue…" /> : (
        <div className="list-card-empty">
          <EmptyArt hue="var(--obj-opportunity)" />
          <p>{q.isError ? 'The queue couldn’t load. Try Refresh.' : 'Nothing waiting: every deal is being worked on.'}</p>
        </div>
      )}
      <footer className="card-foot-split">
        <span />
        <span className="muted small as-of">
          {asOf(q.dataUpdatedAt)}
          <button className="icon-mini" aria-label="Refresh" title="Refresh" onClick={() => void q.refetch()}>
            <svg viewBox="0 0 20 20" aria-hidden="true" className={q.isFetching ? 'spin' : ''}><path d="M16 10a6 6 0 1 1-1.8-4.3M16 3.5v3.7h-3.7" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </span>
      </footer>
    </section>
  );
}

/** The whole Pre-sales queue in one table (opened from the Home card's title). */
export function PresalesQueuePage() {
  const { organization } = useSession();
  const q = useQuery({ queryKey: ['presales-queue', 'all'], queryFn: () => api<{ rows: QueueRow[]; total: number }>('/api/presales/queue?all=1'), refetchInterval: 60_000 });
  const STARTED: Record<string, string> = { in_progress: 'In progress', clarification: 'Waiting for clarification', quotation_ready: 'Quotation ready' };
  const where = (r: QueueRow) => (r.presalesStatus && STARTED[r.presalesStatus] ? STARTED[r.presalesStatus]
    : r.presalesStatus === 'ready' ? (r.presalesUserName ? 'Assigned, not started' : 'To assign')
    : r.hasDocuments ? 'Documents in OneDrive' : 'Waiting for documents');
  return (
    <section>
      <header className="page-head">
        <div><p className="eyebrow"><Link to="/">Home</Link> › Pre-sales queue</p><h1>Pre-sales queue {q.data ? <span className="muted">({q.data.total})</span> : null}</h1></div>
        <Link className="btn" to="/presales">Open My Desk</Link>
      </header>
      <section className="panel">
        {q.isPending ? <p className="loading-line">Loading…</p> : q.isError ? <p className="form-error pad">The queue couldn’t load. Try again.</p> : !q.data.rows.length ? <p className="muted pad">Nothing waiting: every deal is being worked on.</p> : (
          <div className="table-wrap flat">
            <table>
              <thead><tr><th>Deal</th><th>Customer</th><th>Priority</th><th>Where it is</th><th>Engineer</th><th>Source</th><th className="r">Value</th><th>Salesperson</th><th>Added</th></tr></thead>
              <tbody>
                {q.data.rows.map((r) => (
                  <tr key={r.id} className="static">
                    <td><Link to={`/o/opportunity/${r.id}`}>{r.name}</Link></td>
                    <td>{r.accountName ?? '—'}</td>
                    <td>{r.priority === 'high' ? <span className="queue-high">HIGH PRIORITY</span> : r.priorityStatus === 'pending' ? <span className="queue-pending">High priority requested</span> : '—'}</td>
                    <td>{where(r)}{r.supportWaiting ? <div className="small muted"><Icon name="hourglass" />{r.supportWaiting} support not accepted yet</div> : null}{r.openTasks ? <div className="small muted"><Icon name="task" />{r.openTasks} open task{r.openTasks === 1 ? '' : 's'}</div> : null}</td>
                    <td>{r.presalesUserName ?? '—'}</td>
                    <td>{r.dealSource === 'on_hand' ? 'On-Hand' : r.dealSource === 'tender' ? 'Tender' : '—'}</td>
                    <td className="num">{r.amount != null ? formatMoney(r.amount, organization.currency) : '—'}</td>
                    <td>{r.ownerName}</td>
                    <td className="small muted">{formatDate(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Second row: my tasks, recommended actions, recent records
// ---------------------------------------------------------------------------

const TasksArt = () => (
  <svg className="empty-art" viewBox="0 0 240 150" aria-hidden="true">
    <circle cx="120" cy="88" r="72" fill="var(--obj-activity)" opacity="0.2" />
    <rect x="66" y="40" width="118" height="82" rx="8" fill="var(--surface)" stroke="var(--line-2)" />
    <circle cx="84" cy="60" r="7" fill="var(--obj-activity)" />
    <path d="m80.5 60 2.6 2.6 4.6-4.8" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="84" cy="81" r="7" fill="var(--line)" />
    <circle cx="84" cy="102" r="7" fill="var(--line)" />
    <rect x="98" y="57" width="72" height="6" rx="3" fill="var(--line-2)" />
    <rect x="98" y="78" width="72" height="6" rx="3" fill="var(--line)" />
    <rect x="98" y="99" width="72" height="6" rx="3" fill="var(--line)" />
  </svg>
);


type TaskFilter = 'today' | 'overdue' | 'week' | 'all';

function TasksCard() {
  const { user, permissions } = useSession();
  const qc = useQueryClient();
  const [filter, setFilter] = useStored<TaskFilter>(`home.tasks.filter.${user.id}`, 'today');
  // From the attention center ("overdue tasks"): show the overdue ones and scroll to them.
  const [params, setParams] = useSearchParams();
  const cardRef = useRef<HTMLElement>(null);
  const asked = params.get('tasks');
  // Jump once the overdue list has loaded (the cards above change height while loading), just below the top bar.
  const [jumpPending, setJumpPending] = useState(false);
  useEffect(() => {
    // ?tasks=overdue (attention center) or ?tasks=today (the Tasks today figure at the top).
    if (asked !== 'overdue' && asked !== 'today') return;
    setFilter(asked);
    setJumpPending(true);
    const next = new URLSearchParams(params);
    next.delete('tasks');
    setParams(next, { replace: true });
  }, [asked, params, setParams, setFilter]);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const week = new Date(today); week.setDate(week.getDate() + 7);
  // Overdue asks only for tasks due before today, so many tasks due today can never push the overdue ones out of the list.
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  const until = filter === 'week' ? ymd(week) : filter === 'all' ? undefined : filter === 'overdue' ? ymd(yesterday) : ymd(today);
  const tasks = useQuery({
    queryKey: ['my-work', 'tasks', filter, ymd(today)],
    queryFn: () => api<ActivityList>(`/api/activities${qs({ mine: 'true', open: 'true', dueOnOrBefore: until, limit: 200 })}`),
  });
  const items = (tasks.data?.activities ?? [])
    .filter((t) => filter !== 'overdue' || (t.dueDate && t.dueDate < ymd(today)))
    // High priority first, then by due date.
    .slice().sort((a, b) => Number(b.priority === 'high') - Number(a.priority === 'high') || String(a.dueDate ?? '9999').localeCompare(String(b.dueDate ?? '9999')));
  useEffect(() => {
    if (!jumpPending || (filter !== 'overdue' && filter !== 'today') || tasks.isPending) return;
    // A moment for the rest of the page to settle, then land on the card (the flag clears only when it has jumped).
    const t = window.setTimeout(() => { setJumpPending(false); if (cardRef.current) jumpTo(cardRef.current); }, 350);
    return () => window.clearTimeout(t);
  }, [jumpPending, filter, tasks.isPending]);
  const emptyText = { today:'Nothing due today. Plan ahead with New, or enjoy the quiet.', overdue: 'Nothing overdue. Nicely done.', week: 'Nothing due in the next 7 days.', all: 'No open tasks.' }[filter];
  // The last task in the list was just ticked off here: a small "all clear" moment.
  const allClear = !tasks.isPending && items.length === 0;
  useEffect(() => { if (allClear && justFinishedTasks()) celebrate('clear', 'All clear: nicely done'); }, [allClear]);

  // One save at a time: a double click or Enter pressed twice doesn't create the task twice.
  const [saving, setSaving] = useState(false);
  async function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (saving) return;
    const f = Object.fromEntries(new FormData(e.currentTarget).entries()) as Record<string, string>;
    setSaving(true);
    try {
      await api('/api/activities', { method: 'POST', body: { kind: 'task', subtype: 'task', subject: f.subject, dueDate: f.dueDate || null, priority: f.priority, ownerId: user.id } });
      setAdding(false); setError(null);
      void qc.invalidateQueries({ queryKey: ['my-work'] });
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not save the task.'); }
    finally { setSaving(false); }
  }

  return (
    <section className="list-card" aria-label="My tasks" id="tasks" ref={cardRef}>
      <header>
        <ObjectIcon name="activity" />
        <label className="list-card-select">
          <span className="sr-only">Show tasks</span>
          <select value={filter} onChange={(e) => setFilter(e.target.value as TaskFilter)}>
            <option value="today">Today</option>
            <option value="overdue">Overdue</option>
            <option value="week">Next 7 days</option>
            <option value="all">All open tasks</option>
          </select>
        </label>
        {permissions.objects.activity.create && <button className="btn" aria-expanded={adding} onClick={() => setAdding(!adding)}>{adding ? 'Cancel' : 'New'}</button>}
      </header>
      {adding && (
        <form className="quick-task" onSubmit={add}>
          <input name="subject" placeholder="What needs doing?" required maxLength={255} aria-label="Task subject" autoFocus />
          <div className="quick-task-row">
            <input name="dueDate" type="date" defaultValue={ymd(today)} aria-label="Due date" />
            <select name="priority" defaultValue="normal" aria-label="Priority"><option value="high">High</option><option value="normal">Normal</option><option value="low">Low</option></select>
            <button className="btn primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
          {error && <p className="form-error">{error}</p>}
        </form>
      )}
      {items.length ? (
        <div className="list-card-body"><MyWorkList items={items} refs={tasks.data?.refs ?? {}} empty="" /></div>
      ) : (
        <div className="list-card-empty">{tasks.isPending ? <Skeleton shape="list" label="Loading your tasks…" /> : <><TasksArt /><p>{emptyText}</p></>}</div>
      )}
      <footer><button className="link-btn" onClick={() => setFilter('all')}>View all</button></footer>
    </section>
  );
}

function RecentCard() {
  const [all, setAll] = useState(false);
  const recent = useQuery({ queryKey: ['recent'], queryFn: () => api<{ items: RecentItem[] }>('/api/recent').then((r) => r.items) });
  const items = recent.data ?? [];
  return (
    <section className="list-card" aria-labelledby="recent-title">
      <header><h3 id="recent-title">Recent records</h3></header>
      {items.length ? (
        <ul className="recent-list">
          {(all ? items : items.slice(0, 5)).map((r) => (
            <li key={`${r.objectName}:${r.recordId}`}>
              <ObjectIcon name={r.objectName} />
              <span><Link to={`/o/${r.objectName}/${r.recordId}`}>{r.name || '(no name)'}</Link><span className="muted small">{OBJECTS[r.objectName as ObjectName]?.label}</span></span>
            </li>
          ))}
        </ul>
      ) : recent.isPending ? <Skeleton shape="list" label="Loading recent records…" /> : (
        <div className="list-card-empty"><EmptyArt hue="var(--obj-report)" /><p>Records you open will appear here.</p></div>
      )}
      {items.length > 5 && <footer><button className="link-btn" onClick={() => setAll(!all)}>{all ? 'Show fewer' : 'View all'}</button></footer>}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function Card({ title, aside, children, foot }: { title: string; aside?: ReactNode; children: ReactNode; foot?: ReactNode }) {
  return (
    <section className="panel">
      <header className="panel-head"><h3>{title}</h3>{aside}</header>
      {children}
      {foot && <footer className="panel-foot">{foot}</footer>}
    </section>
  );
}

export function Home() {
  const { permissions, schema } = useSession();
  const stages = useStages();
  const pipelines = usePipelines();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [creating, setCreating] = useState<ObjectName | null>(null);
  const canOpp = permissions.objects.opportunity.read;
  const canAct = permissions.objects.activity.read;
  // The target card: this year (the sales targets are yearly), or this quarter (remembered on this computer).
  const [goalPeriod, setGoalPeriod] = useStored<'quarter' | 'year'>('home.goal.period', 'year');

  const start = new Date(); start.setHours(0, 0, 0, 0);
  const end = new Date(start); end.setDate(end.getDate() + 1);

  const events = useQuery({
    queryKey: ['my-work', 'events', ymd(start)],
    enabled: canAct,
    queryFn: () => api<ActivityList>(`/api/activities${qs({ mine: 'true', from: start.toISOString(), to: end.toISOString(), limit: 50 })}`),
  });
  const summary = useQuery({
    queryKey: ['pipeline-summary', 'mine'],
    enabled: canOpp,
    queryFn: () => api<{ stages: { stageId: string; count: number; amount: number }[] }>('/api/pipeline/summary?mine=true').then((r) => r.stages),
  });
  const goal = useQuery({
    queryKey: ['my-goal', goalPeriod],
    enabled: canOpp,
    queryFn: () => api<Goal>(`/api/me/goal?period=${goalPeriod}`),
  });

  const eventList = (events.data?.activities ?? []).slice().sort((a, b) => String(a.startAt).localeCompare(String(b.startAt)));
  // The summary covers the default pipeline.
  const defaultPipeline = pipelines.data?.find((p) => p.isDefault)?.id;
  const open = (stages.data ?? []).filter((s) => !s.isClosed && s.isActive && s.pipelineId === defaultPipeline);
  const rows = open.map((s) => ({ s, v: summary.data?.find((x) => x.stageId === s.id) ?? { count: 0, amount: 0 } }));
  const total = rows.reduce((a, r) => a + r.v.amount, 0);
  const deals = rows.reduce((a, r) => a + r.v.count, 0);
  // Expected value: each stage's value times its probability.
  const expected = rows.reduce((a, r) => a + (r.v.amount * r.s.probability) / 100, 0);
  const stageUrl = (id: string) => `/o/opportunity${qs({ mine: 'true', filter: JSON.stringify([{ field: 'stageId', op: 'eq', value: id }]) })}`;
  const parts = rows.map(({ s, v }) => ({ id: s.id, label: s.name, amount: v.amount, count: v.count, color: stageColor(open, s), to: stageUrl(s.id) }));
  // Leads aren't used: sales see their accounts first; pre-sales (and administrators) see their queue there.
  const listCards = (['account', 'opportunity', 'contact'] as const).filter((o) => permissions.objects[o].read || (o === 'account' && permissions.system.presalesWork));

  return (
    <section className="home">
      <HomeHero open={total} expected={expected} deals={deals} goal={goal.data} period={goalPeriod} onPeriod={setGoalPeriod} />
      <Welcome onCreate={setCreating} />
      <HomeBoard items={[
        { id: 'attention', span: 3, node: <AttentionCard /> },
        ...listCards.map((o) => (
          o === 'account' && permissions.system.presalesWork ? { id: 'presales-queue', node: <PresalesQueueCard /> }
            // Administrators see their projects where others see their contacts.
            : o === 'contact' && permissions.system.manageSetup && permissions.system.manageProjects ? { id: 'projects', node: <ProjectsCard /> }
            : { id: `list-${o}`, node: <MyListCard o={o} onCreate={setCreating} /> }
        )),
        ...(canAct ? [{ id: 'tasks', node: <TasksCard /> }] : []),
        { id: 'recent', node: <RecentCard /> },
        ...(canOpp ? [{ id: 'pipeline', span: 2, node: (
          <Card title="My pipeline by stage" aside={<span className="muted small">{deals} open deal{deals === 1 ? '' : 's'} · {compactSar(total)}</span>}
            foot={<Link to="/o/opportunity?view=kanban">Open the pipeline board</Link>}>
            <div className="pad"><StageBar parts={parts} /></div>
          </Card>
        ) }] : []),
        ...(canAct ? [{ id: 'events', node: (
          <Card title="Today's events">
            {events.isPending ? <Skeleton shape="list" rows={3} label="Loading today’s events…" />
              : <MyWorkList items={eventList} refs={events.data?.refs ?? {}} empty="You're free for the rest of the day." />}
          </Card>
        ) }] : []),
      ]} />
      {creating && (
        <RecordForm
          obj={schema[creating]}
          onClose={() => setCreating(null)}
          onSaved={(r) => {
            const o = creating;
            setCreating(null);
            void qc.invalidateQueries({ queryKey: ['records', o] });
            if (!r.notice) navigate(`/o/${o}/${r.record.id}`, { state: { created: true } });
          }}
        />
      )}
    </section>
  );
}

interface ProjectRow { id: string; number: string; name: string; status: string; accountName: string | null; intel: { health: string; score: number; percent: number; expectedPercent: number | null; warnings: string[] } }

/** Administrators' Home: active projects with health and progress, and what waits for their approval. */
function ProjectsCard() {
  const q = useQuery({ queryKey: ['delivery', 'list', false], queryFn: () => api<{ projects: ProjectRow[] }>('/api/delivery') });
  const po = useQuery({ queryKey: ['delivery', 'po', 'pending'], queryFn: () => api<{ orders: unknown[] }>('/api/delivery/po/pending').then((r) => r.orders.length) });
  const pr = useQuery({ queryKey: ['delivery', 'priority', 'pending'], queryFn: () => api<{ priorities: unknown[] }>('/api/delivery/priority/pending').then((r) => r.priorities.length) });
  const active = (q.data?.projects ?? []).filter((p) => !['closed', 'warranty'].includes(p.status));
  // Critical first, then at risk, then on track.
  const rank: Record<string, number> = { red: 0, yellow: 1, green: 2 };
  const shown = [...active].sort((a, b) => (rank[a.intel.health] ?? 3) - (rank[b.intel.health] ?? 3)).slice(0, 6);
  const waiting = (po.data ?? 0) + (pr.data ?? 0);
  return (
    <section className="list-card" aria-label="Projects">
      <header>
        <span className="obj-icon proj-icon" aria-hidden="true">
          <svg viewBox="0 0 20 20"><path d="M4 5h4v10H4zM10 5h6v4h-6zM10 11h6v4h-6z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>
        </span>
        <h3 className="list-card-title">Projects {q.data ? <span className="muted">({active.length})</span> : null}</h3>
        {waiting > 0 && <Link className="queue-pending" to="/projects">{waiting} waiting for you</Link>}
      </header>
      {shown.length ? (
        <ul className="list-card-rows">
          {shown.map((p) => (
            <li key={p.id}>
              <span className="queue-title">
                <Link to={`/projects/${p.id}`} title={`${p.number} ${p.name}`}>{p.number} {p.name}</Link>
                <span className={`health health-${p.intel.health}`}><span className="health-dot" aria-hidden="true" /></span>
              </span>
              <span className="muted small">
                {[p.accountName, `${p.intel.percent}% done${p.intel.expectedPercent != null ? ` (expected ${p.intel.expectedPercent}%)` : ''}`,
                  p.intel.warnings.length ? `${p.intel.warnings.length} warning${p.intel.warnings.length === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      ) : q.isPending ? <Skeleton shape="list" rows={4} label="Loading projects…" /> : (
        <div className="list-card-empty">
          <EmptyArt hue="var(--accent)" />
          <p>No active projects. Projects start from won deals.</p>
        </div>
      )}
      <footer className="card-foot-split">
        <Link to="/projects">All projects</Link>
        <span className="muted small as-of">{asOf(q.dataUpdatedAt)}</span>
      </footer>
    </section>
  );
}
interface AttentionItem {
  key: string; group: 'approval' | 'attention' | 'idea'; icon: string; text: string; to: string | null;
  decide?: { kind: 'deal_priority' | 'po' | 'vo' | 'project_priority'; id: string };
  /** SAR at stake (deals), unfolds on Home (peek), most urgent card (glow). */
  value?: number; peek?: boolean; glow?: boolean;
}
const DECIDE_URL: Record<string, (id: string) => string> = {
  deal_priority: (id) => `/api/opportunities/${id}/priority/decide`,
  po: (id) => `/api/delivery/po/${id}/decide`,
  vo: (id) => `/api/delivery/vo/${id}/decide`,
  project_priority: (id) => `/api/delivery/priority/${id}/decide`,
};

/** Top of Home: approvals (decided right here), things to follow up, and suggestions. */
function AttentionCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['attention'], queryFn: () => api<{ items: AttentionItem[] }>('/api/attention').then((r) => r.items), refetchInterval: 120_000 });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Motion: a card just decided shrinks away before the list refreshes.
  const [gone, setGone] = useState<Set<string>>(new Set());
  // A row opened (click or Enter): its first items and quick fixes, right under it.
  const [open, setOpen] = useState<string | null>(null);
  // "Fix them" on the Target card opens a row here and brings it into view.
  const sectionRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const onOpen = (e: Event) => {
      const key = (e as CustomEvent<string>).detail;
      setOpen(key);
      window.requestAnimationFrame(() => sectionRef.current?.querySelector(`[data-att="${key}"]`)?.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' }));
    };
    window.addEventListener('crm:attention-open', onOpen);
    return () => window.removeEventListener('crm:attention-open', onOpen);
  }, []);
  // Fixes not yet in the refreshed count (the number counts down at once), and a row just cleared to 0.
  const [less, setLess] = useState<Record<string, number>>({});
  const [cleared, setCleared] = useState<string | null>(null);
  useEffect(() => { setLess({}); }, [q.dataUpdatedAt]);
  const fixed = (key: string, shown: number, undo: boolean) => {
    if (undo) { void qc.invalidateQueries({ queryKey: ['attention'] }); return; }
    setLess((l) => ({ ...l, [key]: (l[key] ?? 0) + 1 }));
    if (shown - 1 <= 0) {
      // The last one: "✓ Cleared" for a moment, then the row folds away.
      setCleared(key); setOpen(null);
      window.setTimeout(() => { void qc.invalidateQueries({ queryKey: ['attention'] }).then(() => setCleared(null)); }, reducedMotion() ? 0 : 1300);
    } else {
      window.setTimeout(() => void qc.invalidateQueries({ queryKey: ['attention'] }), 600);
    }
  };
  const decide = async (it: AttentionItem, approve: boolean) => {
    if (!it.decide) return;
    let comment: string | undefined;
    if (!approve) {
      const c = (await promptDialog('Reason for rejecting?'));
      if (!c?.trim()) return;
      comment = c.trim();
    }
    setBusy(it.key);
    setError(null);
    try {
      await api(DECIDE_URL[it.decide.kind]!(it.decide.id), { method: 'POST', body: { approve, ...(comment ? { comment } : {}) } });
      setGone((g) => new Set(g).add(it.key));
      if (!reducedMotion()) await new Promise((r) => window.setTimeout(r, 420));
      // If the card is still there after the refresh, it shows again.
      void qc.invalidateQueries({ queryKey: ['attention'] }).then(() => setGone((g) => { const n = new Set(g); n.delete(it.key); return n; }));
      void qc.invalidateQueries({ queryKey: ['delivery'] });
      void qc.invalidateQueries({ queryKey: ['popups'] });
    } catch (e) {
      setError((e as Error).message);
    }
    setBusy(null);
  };
  const items = q.data ?? [];
  const groups: { key: AttentionItem['group']; title: string; tone: string }[] = [
    { key: 'approval', title: 'Waiting for your approval', tone: 'att-approval' },
    { key: 'attention', title: 'Needs your attention', tone: 'att-attention' },
  ];
  return (
    <section className="panel attention" aria-labelledby="att-title" ref={sectionRef}>
      <header className="panel-head">
        <h3 id="att-title">Your attention center</h3>
        <span className="muted small">{q.isFetching ? 'Checking…' : asOf(q.dataUpdatedAt)}</span>
      </header>
      {!items.length ? (
        <p className="muted pad">{q.isPending ? 'Loading…' : 'Nothing waits for you: no approvals, nothing late. Well done.'}</p>
      ) : (
        <div className="att-groups">
          {groups.map((g) => {
            // One row per item. Things to follow up: the most money at stake first (the server's order otherwise).
            const inGroup = items.filter((i) => i.group === g.key);
            const list = g.key === 'attention' ? [...inGroup].sort((a, b) => (b.value ?? -1) - (a.value ?? -1)) : inGroup;
            if (!list.length) return null;
            const stake = list.reduce((a, i) => a + (i.value ?? 0), 0);
            const maxValue = Math.max(0, ...list.map((i) => i.value ?? 0));
            const startHere = g.key === 'attention' && list.length > 1 ? list[0]!.key : null;
            return (
              <div key={g.key} className={`att-group ${g.tone}`}>
                <h4>{g.title} <span className="att-count">{list.length}</span>{stake > 0 && <span className="att-stake">{compactSar(stake)} at stake</span>}</h4>
                <ol className="att-ledger">
                  {list.map((it, idx) => {
                    // "6 deals still waiting…" → the number big, the rest as the description.
                    const m = /^(\d[\d,.]*)\s+(.*)$/.exec(it.text);
                    const own = ATT_ICON[it.key];
                    const icon = own
                      ? <span className="obj-icon" style={{ background: own.color }} aria-hidden="true"><svg viewBox="0 0 24 24"><path d={own.path} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                      : it.icon === 'project'
                      ? <span className="obj-icon" style={{ background: '#0b7a75' }} aria-hidden="true"><svg viewBox="0 0 20 20"><path d="M4 5h4v10H4zM10 5h6v4h-6zM10 11h6v4h-6z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg></span>
                      : <ObjectIcon name={it.icon as ObjectName} />;
                    // The money behind it: a bar against the biggest item in the list, and the amount.
                    const stakeCell = (
                      <span className="att-stake-cell">
                        {it.value ? <>
                          <span className="att-bar" aria-hidden="true"><i style={{ inlineSize: `${Math.max(3, (it.value / (maxValue || 1)) * 100)}%` }} /></span>
                          <span className="att-value">{compactSar(it.value)}</span>
                        </> : <span className="att-value none" aria-label="No amount">—</span>}
                      </span>
                    );
                    const desc = (text: string) => (
                      <span className="att-desc">{startHere === it.key && <span className="att-first-tag">Start here</span>}{text}</span>
                    );
                    const rowClass = `att-row${startHere === it.key ? ' att-first' : ''}`;
                    const delay = { animationDelay: `${Math.min(idx, 7) * 30}ms` };
                    if (it.peek && m) {
                      // Opens right under the row: the first items, the quick fixes, "See all".
                      const total = Number((m[1] ?? '').replace(/[,.]/g, '')) || 0;
                      const shown = Math.max(0, total - (less[it.key] ?? 0));
                      const isOpen = open === it.key;
                      if (cleared === it.key) {
                        return <li key={it.key} className="att-row att-cleared" role="status"><span className="att-row-head">{icon}<b className="att-num">✓</b><span className="att-desc">Cleared</span></span></li>;
                      }
                      return (
                        <li key={it.key} className={`${rowClass}${isOpen ? ' open' : ''}`} style={delay} data-att={it.key}>
                          <button type="button" className="att-row-head" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : it.key)}>
                            {icon}<b className="att-num" key={shown}>{shown}</b>{desc(m[2]!)}{stakeCell}
                            <span className="att-chev" aria-hidden="true">▾</span>
                          </button>
                          {isOpen && <AttentionPeek cardKey={it.key} to={it.to} total={shown} onFixed={(undo) => fixed(it.key, shown, undo)} />}
                        </li>
                      );
                    }
                    const body = <>{icon}{m ? <b className="att-num">{m[1]}</b> : <span className="att-num" />}{desc(m ? m[2]! : it.text)}</>;
                    return it.to && !it.decide ? (
                      <li key={it.key} className={rowClass} style={delay}>
                        <Link className="att-row-head" to={it.to}>{body}{stakeCell}<span className="att-chev" aria-hidden="true">→</span></Link>
                      </li>
                    ) : (
                      <li key={it.key} className={`${rowClass}${gone.has(it.key) ? ' leaving' : ''}`} style={delay}>
                        <span className="att-row-head static">
                          {it.to ? <Link className="att-row-link" to={it.to}>{body}</Link> : body}
                          {it.decide ? (
                            <span className="att-actions">
                              <button className="btn small primary" disabled={busy === it.key} onClick={() => void decide(it, true)}>Approve</button>
                              <button className="btn small" disabled={busy === it.key} onClick={() => void decide(it, false)}>Reject</button>
                            </span>
                          ) : stakeCell}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            );
          })}
        </div>
      )}
      {error && <p className="form-error pad-x">{error}</p>}
    </section>
  );
}

interface HomeItem { id: string; node: ReactNode; /** Columns it takes (1–3) on a wide screen. */ span?: number }

/**
 * Home's cards, arranged by each person: "Arrange" → drag a card onto another to swap them (or use the arrows on a
 * phone). Saved for the person, so the order follows them on every device; "Reset" goes back to the default.
 */
function HomeBoard({ items }: { items: HomeItem[] }) {
  const qc = useQueryClient();
  const saved = useQuery({ queryKey: ['home-layout'], queryFn: () => api<{ cards: string[] }>('/api/me/home-layout').then((r) => r.cards), staleTime: 5 * 60_000 });
  const [arranging, setArranging] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const ids = items.map((i) => i.id);
  // Saved order first (cards this person can still see), then any new card in its default place.
  const order = [...(saved.data ?? []).filter((id) => ids.includes(id)), ...ids.filter((id) => !(saved.data ?? []).includes(id))];
  const save = useMutation({
    mutationFn: (cards: string[]) => api('/api/me/home-layout', { method: 'PUT', body: { cards } }),
    onMutate: (cards) => qc.setQueryData(['home-layout'], cards),
    onError: () => void qc.invalidateQueries({ queryKey: ['home-layout'] }),
  });
  const swap = (a: string, b: string) => {
    if (a === b) return;
    const next = [...order];
    const i = next.indexOf(a);
    const j = next.indexOf(b);
    if (i < 0 || j < 0) return;
    [next[i], next[j]] = [next[j]!, next[i]!];
    save.mutate(next);
  };
  const move = (id: string, by: number) => {
    const i = order.indexOf(id);
    const j = i + by;
    if (j >= 0 && j < order.length) swap(id, order[j]!);
  };
  const byId = new Map(items.map((i) => [i.id, i]));
  return (
    <>
      <div className="hb-bar">
        {arranging ? (
          <>
            <span className="small">Drag a card onto another to swap them · اسحب البطاقة وأفلتها على أخرى</span>
            <button className="btn small" onClick={() => save.mutate([])} disabled={save.isPending}>Reset</button>
            <button className="btn small primary" onClick={() => setArranging(false)}>Done</button>
          </>
        ) : <button className="btn small hb-arrange" onClick={() => setArranging(true)} title="Move the cards of your Home page: drag a card onto another to swap them"><Icon name="move" />Arrange my Home</button>}
      </div>
      <div className={`home-board${arranging ? ' arranging' : ''}`}>
        {order.map((id, n) => {
          const it = byId.get(id)!;
          return (
            <div key={id} className={`hb-item span-${it.span ?? 1}${dragId === id ? ' dragging' : ''}${overId === id && dragId && dragId !== id ? ' drop-here' : ''}`}
              draggable={arranging}
              onDragStart={(e) => { setDragId(id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', id); }}
              onDragEnd={() => { setDragId(null); setOverId(null); }}
              onDragOver={(e) => { if (arranging && dragId) { e.preventDefault(); setOverId(id); } }}
              onDragLeave={() => setOverId((o) => (o === id ? null : o))}
              onDrop={(e) => { e.preventDefault(); if (dragId) swap(dragId, id); setDragId(null); setOverId(null); }}>
              {arranging && (
                <div className="hb-handle">
                  <span aria-hidden="true">⠿</span>
                  <button className="icon-mini" aria-label="Move up" disabled={n === 0} onClick={() => move(id, -1)}>↑</button>
                  <button className="icon-mini" aria-label="Move down" disabled={n === order.length - 1} onClick={() => move(id, 1)}>↓</button>
                </div>
              )}
              {it.node}
            </div>
          );
        })}
      </div>
    </>
  );
}